import { create } from "zustand";
import type { UserDTO } from "@algoquest/contracts";

/**
 * appStore (Master Spec §7 — reserved in Phase 00's Zustand architecture
 * alongside sessionStore/algorithmStore/uiStore, built out here in Phase
 * 10): auth/session identity, connectivity status, current top-level
 * screen. No earlier phase needed this — Phase 01-08 had no backend to
 * authenticate against or sync with. `screen` drives §42's flow
 * (BOOT → LOGIN → SYNC → MAIN MENU) at the top of `App.tsx`.
 *
 * Scope decision, stated explicitly rather than silently assumed: there
 * is no full main-menu UI yet — §42's MAIN MENU branches (Campaign,
 * Assignments, Workshop, Practice, Progress, Achievements, Settings) are
 * Phase 11-14+ content. `screen: "play"` here covers what §42 calls both
 * MAIN MENU and PLAY for now, landing directly on the existing HUD/
 * gameplay view Phases 01-08 already built. See the Phase 10 architecture
 * addendum.
 */
export type AppScreen = "boot" | "login" | "sync" | "play";
export type ConnectivityStatus = "online" | "offline" | "syncing";

interface AppState {
  screen: AppScreen;
  user: UserDTO | null;
  connectivity: ConnectivityStatus;
  syncError: string | null;
  persistenceDegraded: boolean;
  setScreen: (screen: AppScreen) => void;
  setUser: (user: UserDTO | null) => void;
  setConnectivity: (status: ConnectivityStatus) => void;
  setSyncError: (error: string | null) => void;
  setPersistenceDegraded: (degraded: boolean) => void;
}

export const useAppStore = create<AppState>((set) => ({
  screen: "boot",
  user: null,
  connectivity: typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "online",
  syncError: null,
  persistenceDegraded: false,
  setScreen: (screen) => set({ screen }),
  setUser: (user) => set({ user }),
  setConnectivity: (connectivity) => set({ connectivity }),
  setSyncError: (syncError) => set({ syncError }),
  setPersistenceDegraded: (persistenceDegraded) => set({ persistenceDegraded }),
}));
